import colorCatalogJson from '../../assets/punksmarket-color-catalog.json';

export interface PunksmarketColorCatalogEntry {
  hex: string;
  alpha?: number;
}

interface ColorCatalogFile {
  version: number;
  source: string;
  colorIdMin: number;
  colorIdMax: number;
  colors: Record<string, PunksmarketColorCatalogEntry>;
}

const catalog = colorCatalogJson as ColorCatalogFile;

const COLOR_BY_ID = new Map<number, PunksmarketColorCatalogEntry>(
  Object.entries(catalog.colors).map(([id, entry]) => [Number(id), entry]),
);

/** RGB hex for a PunksData palette color id (1..221). */
export function getPunksmarketColorHex(colorId: number): string | null {
  const entry = COLOR_BY_ID.get(colorId);
  return entry?.hex ?? null;
}

export function formatPunksmarketColorRef(
  colorId: number,
  options: { includeNumericId?: boolean } = {},
): string {
  const { includeNumericId = true } = options;
  const hex = getPunksmarketColorHex(colorId);
  if (!hex) {
    return `Color #${colorId}`;
  }
  return includeNumericId ? `${hex} (id ${colorId})` : hex;
}

export function formatColorIdList(
  label: string,
  colorIds: number[],
  maxListed = 8,
): string {
  if (colorIds.length === 0) {
    return '';
  }
  const listed = colorIds
    .slice(0, maxListed)
    .map((id) => formatPunksmarketColorRef(id))
    .join(', ');
  const suffix =
    colorIds.length > maxListed
      ? ` (+${colorIds.length - maxListed} more)`
      : '';
  return `${label}: ${listed}${suffix}`;
}

export function shortColorListTag(prefix: string, colorIds: number[]): string | null {
  if (colorIds.length === 0) {
    return null;
  }
  if (colorIds.length === 1) {
    const hex = getPunksmarketColorHex(colorIds[0]);
    return hex ? `${prefix} ${hex}` : `${prefix} #${colorIds[0]}`;
  }
  if (colorIds.length <= 2) {
    return `${prefix}: ${colorIds.map((id) => getPunksmarketColorHex(id) ?? `#${id}`).join(', ')}`;
  }
  return `${prefix} (${colorIds.length})`;
}

export interface PunksmarketColorOption {
  id: number;
  hex: string;
  label: string;
}

/** Palette entries 1..221 for bid criteria pickers. */
export function listPunksmarketColorOptions(): PunksmarketColorOption[] {
  const options: PunksmarketColorOption[] = [];
  for (let id = catalog.colorIdMin; id <= catalog.colorIdMax; id++) {
    const entry = COLOR_BY_ID.get(id);
    if (!entry?.hex) {
      continue;
    }
    options.push({
      id,
      hex: entry.hex,
      label: `${entry.hex} (id ${id})`,
    });
  }
  return options;
}

export function collectColorRefs(
  colorIds: number[],
): Array<{ id: number; hex: string }> {
  const seen = new Set<number>();
  const refs: Array<{ id: number; hex: string }> = [];
  for (const id of colorIds) {
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    const hex = getPunksmarketColorHex(id);
    if (hex) {
      refs.push({ id, hex });
    }
  }
  return refs;
}
